import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Mic, Loader2, Send, Volume2, VolumeX, Hourglass,
  Home, LogIn, PhoneOff, MessageCircle, User as UserIcon, Eye, EyeOff, Layers, ClipboardEdit,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { VidenteDataCorrectionPanel } from "@/components/VidenteDataCorrectionPanel";
import { VidenteRatingWidget } from "@/components/VidenteRatingWidget";
import { VoiceMicWhatsApp } from "@/components/VoiceMicWhatsApp";
import { MiniTutorial } from "@/components/MiniTutorial";
import { CardFan } from "@/components/CardImage";
import { VidenteFarewell } from "@/components/VidenteFarewell";
import { CardDrawRitual } from "@/components/CardDrawRitual";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIDENTE_TUTORIAL_STEPS = [
  {
    emoji: "🎙️",
    title: "Habla con Cristopher",
    desc: "Mantén PRESIONADO el botón dorado del micrófono mientras hablas. Al SOLTARLO, tu voz se envía automáticamente y Cristopher te responde con su voz.",
  },
  {
    emoji: "✋",
    title: "Puedes interrumpirlo",
    desc: "Mientras Cristopher habla, si quieres seguir tú, solo toca el micrófono de nuevo: su voz se corta al instante y empieza a escucharte. Igual al enviar texto: lo interrumpes automáticamente.",
  },
  {
    emoji: "🔒",
    title: "Desliza hacia arriba para fijar",
    desc: "Igual que en WhatsApp: mientras grabas, desliza el dedo hacia ARRIBA para fijar la grabación sin mantener presionado. Después podrás pausar ⏸, reanudar ▶, eliminar 🗑 o enviar ➤ tu mensaje.",
  },
  {
    emoji: "↺",
    title: "Repite cualquier mensaje",
    desc: "Cada mensaje de voz de Cristopher tiene un botón de repetir 🔄: tócalo y vuelve a escucharlo desde el principio cuantas veces quieras. Útil cuando quieres comprobar una predicción exacta.",
  },
  {
    emoji: "🎴",
    title: "El ritual del mazo (9 cartas)",
    desc: "Cuando Cristopher diga que es momento de tirar, el mazo dorado se encenderá. Tócalo y empezará el ritual: elegirás el tipo de mazo (Tarot · Baraja Española · Runas), verás cómo se barajean, partirás el mazo donde sientas y elegirás el lado superior o inferior. Cristopher voltea 9 cartas y las interpreta para ti.",
  },
  {
    emoji: "📓",
    title: "Corrige tus datos si se escucharon mal",
    desc: "El reconocimiento de voz a veces se confunde con nombres o fechas. Usa el botón de la libreta arriba para escribir tu nombre completo, fecha de nacimiento y motivo correctos. Cristopher los retomará en la siguiente respuesta.",
  },
  {
    emoji: "⏳",
    title: "Tu tiempo de sesión",
    desc: "Si el administrador te dio un tiempo limitado, el contador comienza SOLO cuando hablas con Cristopher por primera vez. Verás el tiempo restante en la parte superior.",
  },
  {
    emoji: "⭐",
    title: "Califica al final y lee la respuesta",
    desc: "Al terminar la sesión podrás calificar a Cristopher de 1 a 5 estrellas y dejar tu comentario. Si CrisAngel responde a tu reseña, lo verás como un mensaje fijado dentro de tu sesión la próxima vez que entres.",
  },
  {
    emoji: "✍️",
    title: "¿No puedes hablar?",
    desc: "Usa el campo de texto de abajo para escribirle a Cristopher. Él igualmente te responderá con su voz.",
  },
];

const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const fmtMinutesLabel = (minutes) => {
  if (!minutes) return "";
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m ? `${h}h ${m}min` : `${h}h`;
  }
  return `${minutes}min`;
};

const fmtCountdown = (totalSec) => {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export default function VidenteVirtual() {
  const navigate = useNavigate();
  const { user, isUser, token } = useAuth();
  const isAuthenticated = isUser;

  const [accessLoading, setAccessLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  // Farewell modal — opens once when the user's countdown hits 0 while
  // they're on the page. Reset to false when they regain access.
  const [farewellOpen, setFarewellOpen] = useState(false);
  const [accessUntil, setAccessUntil] = useState(null);
  const [pendingMinutes, setPendingMinutes] = useState(null);
  const [globallyDisabled, setGloballyDisabled] = useState(false);
  const [remainingSec, setRemainingSec] = useState(null);

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [textInput, setTextInput] = useState("");
  const [showText, setShowText] = useState(false); // hide messages by default — voice-first
  const [drawReady, setDrawReady] = useState(false); // AI signalled the cards are ready
  const [askPersonalData, setAskPersonalData] = useState(false); // AI requested data confirmation
  const [showCorrectPanel, setShowCorrectPanel] = useState(false);
  const [personalData, setPersonalData] = useState({});
  const [latestDraw, setLatestDraw] = useState(null); // { phase, cards } from last draw
  const [aiSpeaking, setAiSpeaking] = useState(false); // true while Cristopher's TTS audio is playing
  const [ritualOpen, setRitualOpen] = useState(false); // multi-step animated draw ritual

  const audioPlayerRef = useRef(null);
  const messagesEndRef = useRef(null);

  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  // ---- Access check ----
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!isAuthenticated) {
        if (!cancelled) setAccessLoading(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/vidente/access`, auth);
        if (cancelled) return;
        setHasAccess(!!data.has_access);
        setAccessUntil(data.access_until || null);
        setPendingMinutes(data.pending_minutes || null);
        setGloballyDisabled(data.globally_enabled === false);
      } catch {
        if (!cancelled) setHasAccess(false);
      } finally {
        if (!cancelled) setAccessLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [isAuthenticated, token]);

  // ---- Scroll on new messages ----
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ---- Live countdown once the timer has started ----
  useEffect(() => {
    if (!accessUntil || accessUntil === "unlimited" || accessUntil === "pending") {
      setRemainingSec(null);
      return undefined;
    }
    const expireMs = new Date(accessUntil).getTime();
    if (Number.isNaN(expireMs)) {
      setRemainingSec(null);
      return undefined;
    }
    const tick = () => {
      const ms = expireMs - Date.now();
      if (ms <= 0) {
        setRemainingSec(0);
        setHasAccess(false);
        setFarewellOpen(true);
        return false;
      }
      setRemainingSec(Math.floor(ms / 1000));
      return true;
    };
    if (!tick()) return undefined;
    const id = setInterval(() => {
      if (!tick()) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [accessUntil]);

  // ---- Start / resume session ----
  const startSession = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/vidente/session/start`, {}, auth);
      setSessionId(data.session_id);
      setMessages(data.messages || []);
      setDrawReady(!!data.draw_ready);
      setAskPersonalData(!!data.ask_personal_data);
      setPersonalData(data.personal_data || {});
      if (data.intro && !data.resumed && audioEnabled) {
        // Defer TTS one tick so a slow / hanging speechSynthesis call cannot
        // block React's render commit phase (was suspected of blanking the
        // page on some iOS Safari builds).
        setTimeout(() => {
          try {
            const u = new SpeechSynthesisUtterance(data.intro);
            u.lang = "es-MX";
            u.rate = 0.95;
            u.pitch = 0.95;
            u.onstart = () => setAiSpeaking(true);
            u.onend = () => setAiSpeaking(false);
            u.onerror = () => setAiSpeaking(false);
            window.speechSynthesis?.cancel();
            window.speechSynthesis?.speak(u);
          } catch { /* noop */ }
        }, 60);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la sesión");
    } finally {
      setBusy(false);
    }
  }, [token, audioEnabled]);

  // ---- When AI signals the deck is ready, notify with toast + chat message ----
  const announceDrawReady = useCallback(() => {
    setDrawReady(true);
    toast.success("✨ Cristopher dice: las cartas ya quieren hablar. Toca el mazo cuando estés list@.");
    const nowIso = new Date().toISOString();
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: "🎴 El mazo está listo. Toca las cartas cuando sientas que es el momento.",
        at: nowIso,
        kind: "draw_ready_notice",
      },
    ]);
  }, []);

  // (Removed obsolete playAssistantText helper)

  const sendVoice = async (blob, mimeType) => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const form = new FormData();
      const ext = mimeType.includes("mp4") ? "mp4"
        : mimeType.includes("ogg") ? "ogg"
        : mimeType.includes("mpeg") ? "mp3"
        : "webm";
      form.append("audio", blob, `voice.${ext}`);
      form.append("language", "es");
      form.append("voice", "onyx");
      const { data } = await axios.post(`${API}/vidente/session/${sessionId}/voice`, form, {
        ...auth,
        headers: { ...(auth.headers || {}), "Content-Type": "multipart/form-data" },
      });
      // Append to UI (assistant message carries its audio so user can replay)
      const nowIso = new Date().toISOString();
      setMessages((prev) => [
        ...prev,
        { role: "user", content: data.user_text, at: nowIso },
        { role: "assistant", content: data.reply_text, at: nowIso, audio_b64: data.audio_b64 },
      ]);
      playMp3(data.audio_b64);
      if (data.access_until) setAccessUntil(data.access_until);
      if (data.ready_for_draw && !drawReady) announceDrawReady();
      if (data.ask_personal_data) {
        setAskPersonalData(true);
        toast.info("Cristopher quiere confirmar tus datos. Toca el botón dorado de Corregir datos.");
      }
    } catch (e) {
      if (e?.response?.status === 403) {
        setHasAccess(false);
        toast.error(e?.response?.data?.detail || "Tu tiempo de acceso terminó");
      } else {
        toast.error(e?.response?.data?.detail || "Error procesando el audio");
      }
    } finally {
      setBusy(false);
    }
  };

  const drawPhase = async () => {
    if (!sessionId || busy) return;
    if (!drawReady) {
      toast.info("Cristopher aún no te ha invitado a tirar. Sigue conversando un momento más.");
      return;
    }
    // Open the animated ritual modal — the actual /draw call happens inside,
    // once the user has picked deck + cut + side.
    setRitualOpen(true);
  };

  /**
   * Called by <CardDrawRitual /> after the user has chosen deck + cut + side.
   * Returns the result so the modal can transition to its "reveal" step.
   * Audio playback is deferred until the user clicks "Escuchar la lectura"
   * (handled in onRitualClose below).
   */
  const submitRitualDraw = async ({ phase_id, cut_position, side }) => {
    if (!sessionId) return null;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/vidente/session/${sessionId}/draw`,
        { voice: "onyx", phase_id, cut_position, side },
        auth
      );
      const phase = data.phase || {};
      const cards = (phase.cards || [])
        .map((c) => `${c.name}${c.reversed ? " (invertida)" : ""}`)
        .join(", ");
      const nowIso = new Date().toISOString();
      setLatestDraw({ phase, cards: phase.cards || [], reply_text: data.reply_text, audio_b64: data.audio_b64 });
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `🎴 ${phase.label}: ${cards}\n\n${data.reply_text}`,
          at: nowIso,
        },
      ]);
      // After a draw the backend resets draw_ready until Cristopher invites again.
      setDrawReady(false);
      toast.success(`Tirada de ${phase.label}`);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al tirar las cartas");
      return null;
    } finally {
      setBusy(false);
    }
  };

  // When the ritual closes (user clicked "Escuchar la lectura" or X),
  // play the deferred audio so Cristopher reads while the cards stay on screen.
  const onRitualClose = () => {
    setRitualOpen(false);
    if (latestDraw?.audio_b64) {
      // small delay so the modal exit animation completes first
      setTimeout(() => playMp3(latestDraw.audio_b64), 250);
    }
  };

  const sendText = async () => {
    if (!sessionId || !textInput.trim()) return;
    // Sending a new text message interrupts whatever Cristopher is saying.
    stopAudio();
    setBusy(true);
    const txt = textInput.trim();
    setTextInput("");
    try {
      const { data } = await axios.post(
        `${API}/vidente/session/${sessionId}/text`,
        { text: txt, voice: "onyx" },
        auth
      );
      const nowIso = new Date().toISOString();
      setMessages((prev) => [
        ...prev,
        { role: "user", content: data.user_text, at: nowIso },
        { role: "assistant", content: data.reply_text, at: nowIso, audio_b64: data.audio_b64 },
      ]);
      playMp3(data.audio_b64);
      if (data.access_until) setAccessUntil(data.access_until);
      if (data.ready_for_draw && !drawReady) announceDrawReady();
      if (data.ask_personal_data) {
        setAskPersonalData(true);
        toast.info("Cristopher quiere confirmar tus datos. Toca el botón dorado de Corregir datos.");
      }
    } catch (e) {
      if (e?.response?.status === 403) {
        setHasAccess(false);
        toast.error(e?.response?.data?.detail || "Tu tiempo de acceso terminó");
      } else {
        toast.error(e?.response?.data?.detail || "Error al enviar el mensaje");
      }
    } finally {
      setBusy(false);
    }
  };

  const playMp3 = (b64) => {
    if (!audioEnabled || !b64) return;
    try {
      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      audioPlayerRef.current = audio;
      audio.onended = () => setAiSpeaking(false);
      audio.onpause = () => setAiSpeaking(false);
      audio.onerror = () => setAiSpeaking(false);
      audio.play()
        .then(() => setAiSpeaking(true))
        .catch(() => { setAiSpeaking(false); /* autoplay blocked */ });
    } catch { setAiSpeaking(false); }
  };

  const stopAudio = () => {
    try { audioPlayerRef.current?.pause(); } catch { /* noop */ }
    try { audioPlayerRef.current = null; } catch { /* noop */ }
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    setAiSpeaking(false);
  };

  /** Rewind the currently-playing audio by N seconds (default 10). */
  const rewindAudio = (seconds = 10) => {
    const a = audioPlayerRef.current;
    if (!a) return;
    try {
      a.currentTime = Math.max(0, (a.currentTime || 0) - seconds);
      if (a.paused) a.play().catch(() => { /* noop */ });
    } catch { /* noop */ }
  };

  const endSession = async () => {
    stopAudio();
    if (!sessionId) {
      navigate("/");
      return;
    }
    try {
      await axios.post(`${API}/vidente/session/${sessionId}/end`, {}, auth);
    } catch { /* noop */ }
    setSessionId(null);
    setMessages([]);
    setDrawReady(false);
    toast.success("Sesión cerrada");
  };

  // ====== RENDER ======

  if (!isAuthenticated) {
    return (
      <GateScreen
        title="Necesitas iniciar sesión"
        subtitle="La Vidente Virtual es exclusiva para miembros registrados."
        ctaLabel="Iniciar Sesión"
        ctaIcon={<LogIn className="w-4 h-4" />}
        ctaTo="/ingresar"
      />
    );
  }

  if (accessLoading) {
    return (
      <div className="min-h-screen bg-[#070708] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <>
        <GateScreen
          title="Vidente Virtual"
          subtitle={
            globallyDisabled
              ? "El Vidente Virtual está temporalmente desactivado por el administrador. Vuelve a intentarlo más tarde."
              : remainingSec === 0
              ? "Tu tiempo de acceso terminó. Solicita al administrador que reinicie tu sesión para continuar."
              : "Esta experiencia es exclusiva. Solicita acceso al administrador y serás notificad@ cuando se habilite tu sesión."
          }
          ctaLabel="Ir al inicio"
          ctaIcon={<Home className="w-4 h-4" />}
          ctaTo="/"
          secondaryLabel="Contactar por WhatsApp"
          secondaryHref="https://wa.me/526622870805?text=Hola%20Cristopher%2C%20quiero%20acceso%20a%20la%20Vidente%20Virtual."
        />
        <VidenteFarewell open={farewellOpen} onClose={() => setFarewellOpen(false)} />
      </>
    );
  }

  // Access granted — show session UI
  return (
    <div className="min-h-screen bg-[#070708] text-neutral-100 relative overflow-hidden">
      {/* Background mystical glow */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#d4af37]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-700/15 rounded-full blur-[120px]" />
      </div>

      <header className="relative z-10 px-6 py-4 border-b border-white/5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80">
          <Sparkles className="w-5 h-5 text-[#d4af37]" />
          <span className="font-serif text-xl">CrisAngel Astral</span>
        </Link>
        <div className="flex items-center gap-2">
          {remainingSec != null && remainingSec > 0 && (
            <span
              data-testid="vidente-countdown"
              className={`inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-full border ${
                remainingSec < 120
                  ? "border-red-500/60 text-red-300 bg-red-500/10 animate-pulse"
                  : "border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/10"
              }`}
              title="Tiempo restante de tu sesión"
            >
              <Hourglass className="w-3.5 h-3.5" /> {fmtCountdown(remainingSec)}
            </span>
          )}
          {accessUntil === "pending" && (
            <span
              data-testid="vidente-pending-chip"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-300 bg-amber-500/10"
              title="Tu tiempo comenzará al hablar con Cristopher por primera vez"
            >
              <Hourglass className="w-3 h-3" /> {pendingMinutes ? `${fmtMinutesLabel(pendingMinutes)} listos` : "Listo para iniciar"}
            </span>
          )}
          <Link
            to="/"
            data-testid="btn-go-home"
            title="Ir al inicio"
            className="p-2 rounded-full hover:bg-white/5 text-neutral-300 border border-white/10"
          >
            <Home className="w-4 h-4" />
          </Link>
          <Link
            to="/mi-cuenta"
            data-testid="btn-go-account"
            title="Mi Cuenta"
            className="p-2 rounded-full hover:bg-white/5 text-neutral-300 border border-white/10"
          >
            <UserIcon className="w-4 h-4" />
          </Link>
          {sessionId && (
            <button
              data-testid="toggle-text"
              onClick={() => setShowText((v) => !v)}
              className="p-2 rounded-full hover:bg-white/5 text-neutral-300 border border-white/10"
              title={showText ? "Ocultar mensajes" : "Mostrar mensajes"}
            >
              {showText ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          )}
          <button
            data-testid="toggle-audio"
            onClick={() => {
              if (audioEnabled) stopAudio();
              setAudioEnabled((v) => !v);
            }}
            className="p-2 rounded-full hover:bg-white/5 text-neutral-300 border border-white/10"
            title={audioEnabled ? "Silenciar" : "Activar audio"}
          >
            {audioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          {sessionId && (
            <button
              data-testid="btn-end-session"
              onClick={endSession}
              className="text-xs uppercase tracking-widest border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-full px-4 py-2 flex items-center gap-2"
            >
              <PhoneOff className="w-3.5 h-3.5" /> Cerrar
            </button>
          )}
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {!sessionId ? (
          <div className="text-center py-20">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-28 h-28 mx-auto rounded-full bg-gradient-to-br from-[#d4af37]/30 to-purple-700/30 flex items-center justify-center border border-[#d4af37]/40 mb-6">
                <Sparkles className="w-14 h-14 text-[#d4af37]" />
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl text-white mb-3">Vidente Virtual</h1>
              <p className="text-neutral-400 max-w-md mx-auto mb-2">
                Conversa por voz en vivo con Cristopher. Habla y él te responde con su voz.
              </p>
              {accessUntil === "pending" ? (
                <p className="text-xs text-amber-300/90 mb-6 max-w-sm mx-auto" data-testid="access-pending-notice">
                  ⏳ {pendingMinutes ? <>Tienes <b>{fmtMinutesLabel(pendingMinutes)}</b> de acceso. </> : null}
                  Tu tiempo comenzará a contar cuando hables con Cristopher por primera vez.
                </p>
              ) : accessUntil && accessUntil !== "unlimited" ? (
                <p className="text-xs text-[#d4af37]/80 mb-6" data-testid="access-until">
                  Acceso hasta: {new Date(accessUntil).toLocaleString("es-MX")}
                </p>
              ) : null}
              {accessUntil === "unlimited" && (
                <p className="text-xs text-[#d4af37]/80 mb-6">Acceso ilimitado</p>
              )}
              <button
                data-testid="btn-start-vidente"
                onClick={startSession}
                disabled={busy}
                className="btn-gold rounded-full px-8 py-4 uppercase tracking-[0.2em] text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                Iniciar Sesión por Voz
              </button>
              <p className="text-[11px] text-neutral-500 mt-6 max-w-md mx-auto">
                Necesitarás permitir acceso al micrófono. Si tu dispositivo no permite grabar, podrás escribir como respaldo.
              </p>
            </motion.div>
          </div>
        ) : (
          <>
            {/* Voice-first immersive view (default) */}
            {!showText && (
              <div
                data-testid="vidente-voice-orb"
                className="flex flex-col items-center justify-center text-center py-8 mb-2 min-h-[55vh]"
              >
                <motion.div
                  animate={{
                    scale: (busy || aiSpeaking) ? [1, 1.06, 1] : 1,
                    boxShadow: (busy || aiSpeaking)
                      ? [
                          "0 0 60px rgba(212,175,55,0.35)",
                          "0 0 100px rgba(168,85,247,0.55)",
                          "0 0 60px rgba(212,175,55,0.35)",
                        ]
                      : "0 0 80px rgba(212,175,55,0.30)",
                  }}
                  transition={{ duration: 1.8, repeat: (busy || aiSpeaking) ? Infinity : 0, ease: "easeInOut" }}
                  className="w-44 h-44 sm:w-52 sm:h-52 rounded-full bg-gradient-to-br from-[#d4af37]/30 via-purple-700/30 to-black/40 border border-[#d4af37]/50 flex items-center justify-center mb-8 relative"
                >
                  <Sparkles className="w-20 h-20 text-[#d4af37]" />
                  {(busy || aiSpeaking) && (
                    <span className="absolute inset-0 rounded-full border-2 border-[#d4af37]/40 animate-ping" />
                  )}
                </motion.div>
                <p className="text-sm text-neutral-300 max-w-md" data-testid="vidente-status-text">
                  {busy
                    ? "Cristopher está pensando..."
                    : aiSpeaking
                    ? "Cristopher está hablando · toca el micro para interrumpir"
                    : messages.length > 0
                    ? "Mantén presionado el micrófono y habla. Cristopher escucha tu voz."
                    : "Tu sesión comenzó. Mantén presionado el micrófono y preséntate."}
                </p>
                <p className="text-[11px] uppercase tracking-widest text-[#d4af37]/70 mt-3">
                  Modo voz · audio prioritario
                </p>
                {messages.length > 0 && (
                  <button
                    data-testid="btn-show-messages"
                    onClick={() => setShowText(true)}
                    className="mt-6 text-xs uppercase tracking-widest text-neutral-400 hover:text-[#d4af37] inline-flex items-center gap-2"
                  >
                    <Eye className="w-3.5 h-3.5" /> Mostrar mensajes ({messages.length})
                  </button>
                )}
              </div>
            )}

            {/* Conversation (only when toggled visible) */}
            {showText && (
              <div
                data-testid="vidente-conversation"
                className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur p-4 sm:p-6 mb-4 min-h-[55vh] max-h-[65vh] overflow-y-auto space-y-4"
              >
                <AnimatePresence initial={false}>
                  {messages.map((m, i) => (
                    <motion.div
                      key={`${m.at || "msg"}-${i}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                      className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                      data-testid={`msg-${m.role}-${i}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                          m.role === "user"
                            ? "bg-[#d4af37]/15 border border-[#d4af37]/30 text-white rounded-br-sm"
                            : "bg-white/[0.04] border border-white/10 text-neutral-100 rounded-bl-sm"
                        }`}
                      >
                        {m.role === "assistant" && (
                          <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/90 mb-1 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Cristopher
                          </div>
                        )}
                        <p className="whitespace-pre-wrap">{m.content}</p>
                        <div className="text-[10px] text-neutral-500 mt-1 flex items-center justify-between gap-2">
                          <span>{fmtTime(m.at)}</span>
                          {m.role === "assistant" && m.audio_b64 && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => rewindAudio(10)}
                                title="Atrás 10s"
                                data-testid={`audio-rewind-${i}`}
                                className="px-1.5 py-0.5 rounded hover:bg-[#d4af37]/15 text-[#d4af37]"
                              >
                                ⏪10s
                              </button>
                              <button
                                onClick={() => playMp3(m.audio_b64)}
                                title="Reproducir de nuevo"
                                data-testid={`audio-replay-${i}`}
                                className="px-1.5 py-0.5 rounded hover:bg-[#d4af37]/15 text-[#d4af37]"
                              >
                                ▶ repetir
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {busy && (
                  <div className="flex items-center gap-2 text-xs text-[#d4af37]/80 italic">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cristopher está respondiendo...
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}

            {/* Latest tirada — visual card fan */}
            {latestDraw?.cards?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6"
                data-testid="latest-draw"
              >
                <div className="text-center text-[11px] uppercase tracking-[0.3em] text-[#d4af37] mb-3">
                  🎴 {latestDraw.phase?.label || "Tirada"}
                </div>
                <CardFan cards={latestDraw.cards} size="sm" />
              </motion.div>
            )}

            {/* Rating widget — visible once there's some interaction */}
            {sessionId && messages.length >= 4 && (
              <VidenteRatingWidget sessionId={sessionId} token={token} />
            )}

            {/* Voice control — WhatsApp style */}
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-end gap-4">
                <VoiceMicWhatsApp busy={busy} disabled={!sessionId} onSend={sendVoice} onRecordStart={stopAudio} />

                <motion.button
                  data-testid="btn-draw-phase"
                  onClick={drawPhase}
                  disabled={busy || !drawReady}
                  title={drawReady ? "Tirar las cartas (Cristopher ya te invitó)" : "Cristopher decidirá cuándo es el momento de tirar"}
                  animate={drawReady ? {
                    boxShadow: [
                      "0 0 0px rgba(212,175,55,0.0)",
                      "0 0 30px rgba(212,175,55,0.85)",
                      "0 0 0px rgba(212,175,55,0.0)",
                    ],
                  } : { boxShadow: "0 0 0px rgba(212,175,55,0)" }}
                  transition={{ duration: 1.8, repeat: drawReady ? Infinity : 0, ease: "easeInOut" }}
                  className={`w-16 h-16 rounded-full border-2 transition-all flex items-center justify-center ${
                    drawReady
                      ? "border-[#d4af37] bg-gradient-to-br from-[#d4af37]/30 to-purple-500/30 hover:scale-110 cursor-pointer"
                      : "border-white/15 bg-white/[0.03] opacity-40 cursor-not-allowed"
                  } ${busy ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <Layers className={`w-7 h-7 ${drawReady ? "text-[#d4af37]" : "text-neutral-500"}`} />
                </motion.button>

                <motion.button
                  data-testid="btn-correct-data"
                  onClick={() => setShowCorrectPanel(true)}
                  disabled={busy}
                  title="Corregir datos personales que Cristopher haya entendido mal"
                  animate={askPersonalData ? {
                    boxShadow: [
                      "0 0 0px rgba(212,175,55,0.0)",
                      "0 0 24px rgba(212,175,55,0.7)",
                      "0 0 0px rgba(212,175,55,0.0)",
                    ],
                  } : { boxShadow: "0 0 0px rgba(212,175,55,0)" }}
                  transition={{ duration: 1.8, repeat: askPersonalData ? Infinity : 0, ease: "easeInOut" }}
                  className={`w-16 h-16 rounded-full border-2 transition-all flex items-center justify-center ${
                    askPersonalData
                      ? "border-[#d4af37] bg-gradient-to-br from-[#d4af37]/30 to-amber-500/30 hover:scale-110"
                      : "border-white/15 bg-white/[0.03] hover:border-[#d4af37]/40 hover:bg-white/5"
                  } ${busy ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <ClipboardEdit className={`w-7 h-7 ${askPersonalData ? "text-[#d4af37]" : "text-neutral-400"}`} />
                </motion.button>
              </div>
              <p className="text-xs text-neutral-400 text-center max-w-md">
                {busy
                  ? "..."
                  : aiSpeaking
                  ? "Cristopher está hablando · toca el micro para interrumpirlo y seguir hablando"
                  : drawReady
                  ? "Mantén presionado el micro para hablar (desliza ⬆ para fijar) · el mazo dorado ya está listo, tócalo cuando quieras"
                  : "Mantén presionado el micro para hablar · suelta para enviar · desliza ⬆ para fijar la grabación"}
              </p>
            </div>

            {/* Text fallback */}
            <div className="mt-6 flex items-center gap-2">
              <input
                data-testid="text-fallback-input"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendText();
                  }
                }}
                disabled={busy}
                placeholder="O escribe si no puedes hablar..."
                className="flex-1 bg-black/60 border border-white/10 rounded-full px-4 py-2.5 text-sm text-neutral-100 placeholder:text-neutral-600 focus:outline-none focus:border-[#d4af37]/60"
              />
              <button
                data-testid="btn-send-text"
                onClick={sendText}
                disabled={busy || !textInput.trim()}
                className="rounded-full p-2.5 bg-[#d4af37]/20 border border-[#d4af37]/40 hover:bg-[#d4af37]/30 disabled:opacity-40"
              >
                <Send className="w-4 h-4 text-[#d4af37]" />
              </button>
            </div>
          </>
        )}
      </main>

      <MiniTutorial
        storageKey="ca_tut_vidente_v2"
        title="Vidente Virtual"
        steps={VIDENTE_TUTORIAL_STEPS}
      />

      <VidenteDataCorrectionPanel
        open={showCorrectPanel}
        onClose={() => setShowCorrectPanel(false)}
        sessionId={sessionId}
        token={token}
        initial={personalData}
        onSaved={(updated) => {
          setPersonalData(updated);
          setAskPersonalData(false);
        }}
      />

      <CardDrawRitual
        open={ritualOpen}
        onClose={onRitualClose}
        onSubmit={submitRitualDraw}
        busy={busy}
      />
    </div>
  );
}

const GateScreen = ({ title, subtitle, ctaLabel, ctaIcon, ctaTo, secondaryLabel, secondaryHref }) => (
  <div className="min-h-screen bg-[#070708] text-neutral-100 flex items-center justify-center px-6">
    <div className="max-w-md w-full text-center">
      <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#d4af37]/20 to-purple-700/20 flex items-center justify-center border border-[#d4af37]/30 mb-6">
        <Sparkles className="w-12 h-12 text-[#d4af37]" />
      </div>
      <h1 className="font-serif text-3xl text-white mb-3">{title}</h1>
      <p className="text-neutral-400 mb-8">{subtitle}</p>
      <Link
        to={ctaTo}
        className="btn-gold rounded-full px-6 py-3 uppercase tracking-[0.2em] text-xs font-semibold inline-flex items-center gap-2"
        data-testid="vidente-gate-cta"
      >
        {ctaIcon} {ctaLabel}
      </Link>
      {secondaryHref && (
        <div className="mt-4">
          <a
            href={secondaryHref}
            target="_blank"
            rel="noreferrer"
            className="text-xs uppercase tracking-widest text-[#d4af37] hover:text-white inline-flex items-center gap-2"
            data-testid="vidente-gate-secondary"
          >
            <MessageCircle className="w-3.5 h-3.5" /> {secondaryLabel}
          </a>
        </div>
      )}
    </div>
  </div>
);
